// THE VR LOOK — distance: THE VOID (APP_VRSTYLE.md "The void"). The owner (2026-11-03): "set the background to the
// most common colour and render less pixels" — the world is drawn to D m (vrsD, default 1500) and no farther:
//   - the camera's far plane = D while an XR session presents (three copies camera.far into the session's depthFar
//     and every eye projection: the frustum culls EVERY family past D on the CPU — ring, far ground, buildings,
//     structures, lifts, water, trees — and the GPU clips the rest);
//   - the scene FogExp2 runs to the background colour vrsBg on the way: density VRS_FOG_K / D (≈ 99.5 % at D,
//     ≈ 44 % at D/3, ≈ 19 % at D/5), so the cut at D is invisible;
//   - the sky dome's horizon (bot) = the background, its mid band pulled toward it: the void and the sky meet
//     without a seam; the zenith keeps its blue.
// Outside a session (the Quest's 2D page: Map, beam-in) the far plane is the camera's own (the flight sees the
// valley); the fog + sky terms hold throughout. vrsInit (main.js, once the class is decided) also sets the facades'
// flat mean (buildings.js) and the crowns' sun (trees.js); vrsFogDensity is the tunables re-apply's density;
// vrsTune({d, bg}) re-applies live (dev: __vr.vrstyle({d, bg})).
import * as THREE from 'three';
import { skyU } from '../sky.js';
import { vrStyleOn, vrsD, vrsBg, vrsSetVoid, vrsFoveation, vrsEyeScale, vrsLayerQuality } from './index.js';
import { vrsTreeSun } from './trees.js';
import { vrsBuildings } from './buildings.js';

export const VRS_FOG_K = 2.3;      // fog density × D: 1 − exp(−2.3²) = 99.5 % at D
export const VRS_SKY_MID = 0.6;    // how far the dome's mid band moves toward the background

let _ref = null;                   // { scene, camera, renderer, far0, mid0 }

// the scene's fog density for a tunables value `d` (main.js applyTunables): the void's under the style, else d
export function vrsFogDensity(d) { return vrStyleOn() ? VRS_FOG_K / vrsD() : d; }

function presenting() { return !!(_ref && _ref.renderer && _ref.renderer.xr && _ref.renderer.xr.isPresenting); }

function applyVoid() {
  if (!_ref) return;
  const { scene, camera } = _ref;
  const d = vrsD(), bg = new THREE.Color(vrsBg());
  if (scene && scene.fog) { scene.fog.color.copy(bg); scene.fog.density = VRS_FOG_K / d; }
  const u = skyU();
  u.bot.value.copy(bg);
  u.mid.value.copy(_ref.mid0).lerp(bg, VRS_SKY_MID);
  // the projection layer's quality (vrsLayerQuality: the compositor's filtering of the eye buffer; 'default' = as is)
  // (three's layer: the session's renderState takes it only at the next frame)
  const lay = presenting() && _ref.renderer.xr.getBaseLayer ? _ref.renderer.xr.getBaseLayer() : null, q = vrsLayerQuality();
  if (lay && 'quality' in lay && q && lay.quality !== q) { try { lay.quality = q; } catch (e) { console.warn(`[vrstyle] layer quality ${q}: ${e && e.message}`); } }
  if (camera) {
    const f = presenting() ? d : _ref.far0;
    if (camera.far !== f) { camera.far = f; camera.updateProjectionMatrix(); }
  }
}

// once, after detectDevice (main.js): the style's scene-wide terms; false (and nothing done) off
export function vrsInit({ scene, meta, camera, renderer } = {}) {
  if (!vrStyleOn()) return false;
  _ref = { scene, camera, renderer, far0: camera ? camera.far : null, mid0: skyU().mid.value.clone() };
  // the layer's fixed foveation: main.js sets it BEFORE detectDevice (the class unknown: renderconfig's 1.0, the
  // headset's layer reported fixedFoveation 1 on the live w55 build) — the style's value now, before any session
  if (renderer && renderer.xr && renderer.xr.setFoveation) renderer.xr.setFoveation(vrsFoveation(1));
  // the eye buffer's size (vrsEyeScale; the layer reads it at a session's start, so never while presenting) — over
  // adaptres.js's renderconfig framebuffer_scale (1.0, set at its install, before this)
  if (renderer && renderer.xr && renderer.xr.setFramebufferScaleFactor && !renderer.xr.isPresenting) renderer.xr.setFramebufferScaleFactor(vrsEyeScale());
  if (renderer && renderer.xr) {
    renderer.xr.addEventListener('sessionstart', applyVoid);   // three sets isPresenting before either event
    renderer.xr.addEventListener('sessionend', applyVoid);
  }
  applyVoid();
  vrsTreeSun(meta);
  vrsBuildings();
  console.info(`[vrstyle] the flat VR look is on (?vrstyle=0 = the old look); the void at ${vrsD()} m; eye buffer ×${vrsEyeScale()}, foveation ${vrsFoveation(1)}`);
  return true;
}

// main.js: the far ring's border + the horizon's peak islands (all past the void) are not drawn under the style
export function vrsHideFar(objs) {
  if (!vrStyleOn()) return;
  for (const o of objs) if (o) o.visible = false;
}

// live (dev / the headset A/B): a new D and / or background, re-applied now
export function vrsTune(o) {
  if (!vrStyleOn() || !o) return null;
  vrsSetVoid(o);
  applyVoid();
  return { d: vrsD(), bg: vrsBg().toString(16), far: _ref && _ref.camera ? _ref.camera.far : null };
}
